// Radha — e-invoicing regime lookup (072).
//
// The panel that used to be hardcoded to India's IRP reads its vocabulary from
// here instead: what the tax identifier is called, what the cleared invoice's
// identifier is called, and whether the country has a mandate at all.
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type ClearanceModel = "clearance" | "post_audit" | "none";

export interface EinvoiceRegime {
  country: string;
  regime_name: string;
  model: ClearanceModel;
  network: string | null;
  tax_id_label: string;
  clearance_id_label: string | null;
  requires_qr: boolean;
  requires_signature: boolean;
  mandatory_above_turnover: string | null;
  turnover_currency: string | null;
  statute_ref: string | null;
  phase_note: string | null;
}

/**
 * The regime for one country. Returns null (not an error) for a country with
 * no row — an unmapped jurisdiction is a gap in the pack, not a failure, and
 * the panel degrades to a plain tax summary.
 */
export function useEinvoiceRegime(country: string | null | undefined) {
  return useQuery({
    queryKey: ["einvoice-regime", country ?? ""],
    enabled: !!country,
    queryFn: async (): Promise<EinvoiceRegime | null> => {
      const { data, error } = await supabase
        .from("einvoice_regimes")
        .select("country, regime_name, model, network, tax_id_label, clearance_id_label, requires_qr, requires_signature, mandatory_above_turnover, turnover_currency, statute_ref, phase_note")
        .eq("country", country)
        .maybeSingle();
      if (error) throw error;
      return (data as EinvoiceRegime) ?? null;
    },
    staleTime: 600_000,
  });
}
